import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  Elements,
  PaymentElement,
  useStripe,
  useElements,
} from '@stripe/react-stripe-js'
import type { StripeElementsOptions } from '@stripe/stripe-js'
import { loadStripe } from '@stripe/stripe-js'
import { Loader2 } from 'lucide-react'
import getStripe from '@/lib/stripe'
import type { AnswerMap, AnswerValue, PhaseInfo, WorkflowStage } from '../../types/workflow'
import { API_BASE_URL } from '@/lib/apiConfig';

const STRIPE_PK = import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY

interface PaymentMethodTemplateProps {
  stage: WorkflowStage
  answers: AnswerMap
  currentPhase?: PhaseInfo | null
  onSubmit: () => Promise<void>
  onSelect: (value: AnswerValue) => void
}

const API_BASE_URL = (
  import.meta.env.VITE_API_BASE_URL ||
  API_BASE_URL
).replace(/\/+$/, '')

// ── Inner form (must be inside Elements provider) ───────────────────────────

function PaymentFormInner({
  onPaymentConfirmed,
}: {
  onPaymentConfirmed: (paymentMethodId: string, setupIntentId: string) => void
}) {
  const stripe = useStripe()
  const elements = useElements()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  // Stripe's <PaymentElement> mounts an iframe asynchronously. `useStripe`
  // and `useElements` return truthy BEFORE the PaymentElement's iframe
  // finishes mounting, so gating submit only on those lets the user click
  // while the element is still mounting. `stripe.confirmSetup({elements})`
  // then fails with "We could not retrieve data from the specified
  // Element. … ready event has been emitted." We gate on the element's
  // own `ready` event to avoid this race (mirrors shared/PaymentStep.tsx).
  const [isElementReady, setIsElementReady] = useState(false)

  const canSubmit = !!(stripe && elements && isElementReady && !isSubmitting)

  const handleSubmit = async () => {
    if (!canSubmit || !stripe || !elements) return

    setIsSubmitting(true)
    setErrorMessage(null)

    try {
      const { error, setupIntent } = await stripe.confirmSetup({
        elements,
        redirect: 'if_required',
      })

      if (error) {
        setErrorMessage(error.message || 'An unexpected error occurred.')
      } else if (setupIntent?.payment_method) {
        const pmId =
          typeof setupIntent.payment_method === 'string'
            ? setupIntent.payment_method
            : setupIntent.payment_method.id
        onPaymentConfirmed(pmId, setupIntent.id)
      }
    } catch (err) {
      const message =
        err && typeof err === 'object' && 'message' in err
          ? (err as { message: string }).message
          : 'Payment confirmation failed.'
      setErrorMessage(message)
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className="flex flex-col gap-5">
      <PaymentElement
        options={{ layout: 'tabs' }}
        onReady={() => {
          setIsElementReady(true)
          setErrorMessage(null)
        }}
        onChange={(e) => {
          if (e.complete) setErrorMessage(null)
        }}
      />

      {errorMessage && (
        <div className="rounded-xl border border-red-500/30 bg-red-500/10 p-4 text-center text-sm text-red-300">
          {errorMessage}
        </div>
      )}

      <div className="mt-auto pt-4 pb-2">
        <button
          type="button"
          disabled={!canSubmit}
          onClick={() => { void handleSubmit() }}
          className="flex w-full py-4 px-6 items-center justify-center gap-2 text-[15px] font-semibold text-[var(--ww-btn-text)] bg-[var(--ww-btn-bg)] border-none rounded-full cursor-pointer transition-all duration-300 shadow-[0_2px_8px_rgba(0,0,0,0.1)] hover:not-disabled:bg-[var(--ww-btn-hover)] hover:not-disabled:-translate-y-0.5 disabled:opacity-25 disabled:cursor-default"
        >
          {!isElementReady ? (
            <>
              <Loader2 className="h-5 w-5 animate-spin" />
              Loading payment form…
            </>
          ) : isSubmitting ? (
            <>
              <Loader2 className="h-5 w-5 animate-spin" />
              Processing...
            </>
          ) : (
            'Submit My Assessment'
          )}
        </button>
      </div>
    </div>
  )
}

// ── Outer template component ────────────────────────────────────────────────

export default function PaymentMethodTemplate({
  answers,
  onSubmit,
  onSelect,
}: PaymentMethodTemplateProps) {
  const [intakeDetails, setIntakeDetails] = useState<{ isGuided: boolean; paymentStatus: string | null } | null>(null)
  const [isSubmittingDirect, setIsSubmittingDirect] = useState(false)
  const [clientSecret, setClientSecret] = useState<string | null>(null)
  const [connectedAccount, setConnectedAccount] = useState<string | null>(null)
  // Customer Session client_secret — required in Elements options for
  // PaymentElement to surface the patient's saved cards. Null when the
  // backend couldn't mint a session (falls back to blank card form).
  const [customerSessionClientSecret, setCustomerSessionClientSecret] =
    useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [paymentConfirmed, setPaymentConfirmed] = useState(false)
  const [retryCount, setRetryCount] = useState(0)

  const intakeToken = answers.intake_token as string | undefined

  // Step 1: Fetch pending intake details to determine if guided
  useEffect(() => {
    if (!intakeToken) {
      setIntakeDetails({ isGuided: false, paymentStatus: null })
      setIsLoading(false)
      return
    }

    const fetchIntakeDetails = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/intakes/validate/${intakeToken}`)
        const data = await response.json()

        if (response.ok && data?.success) {
          setIntakeDetails({ isGuided: data.data.isGuided ?? false, paymentStatus: data.data.paymentStatus ?? null })
        } else {
          setIntakeDetails({ isGuided: false, paymentStatus: null })
        }
      } catch {
        setIntakeDetails({ isGuided: false, paymentStatus: null })
      }
    }

    void fetchIntakeDetails()
  }, [intakeToken])

  // Step 2: For guided intakes, fetch SetupIntent for Stripe payment
  useEffect(() => {
    if (!intakeDetails) return
    if (!intakeDetails.isGuided) {
      setIsLoading(false)
      return
    }

    // Payment already held or captured (storefront flow) — no need to collect payment again
    const alreadyPaid = intakeDetails.paymentStatus === 'hold_placed' || intakeDetails.paymentStatus === 'captured'
    if (alreadyPaid) {
      setIsLoading(false)
      return
    }

    if (!intakeToken) {
      setError('Missing intake token. Please restart the intake.')
      setIsLoading(false)
      return
    }

    setIsLoading(true)
    setError(null)

    const fetchSetupIntent = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/intakes/setup-intent/${intakeToken}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
        })
        const data = await response.json()

        if (!response.ok || !data?.success) {
          throw new Error(data?.error?.message || data?.message || 'Failed to initialize payment')
        }

        setClientSecret(data.data.clientSecret)
        setConnectedAccount(data.data.stripeAccount || null)
        setCustomerSessionClientSecret(data.data.customerSessionClientSecret ?? null)
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to initialize payment setup')
      } finally {
        setIsLoading(false)
      }
    }

    void fetchSetupIntent()
  }, [intakeToken, retryCount, intakeDetails])

  const stripePromise = useMemo(() => {
    if (!intakeDetails?.isGuided) return null
    if (connectedAccount && STRIPE_PK) {
      return loadStripe(STRIPE_PK, { stripeAccount: connectedAccount })
    }
    return getStripe()
  }, [connectedAccount, intakeDetails?.isGuided])

  const handlePaymentConfirmed = useCallback(
    async (paymentMethodId: string, setupIntentId: string) => {
      onSelect({ paymentMethodId, setupIntentId })
      setPaymentConfirmed(true)
      await onSubmit()
    },
    [onSelect, onSubmit],
  )

  // ── Still loading intake details ────────────────────────────────────────
  if (!intakeDetails || isLoading) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center gap-3 py-10">
        <Loader2 className="h-8 w-8 animate-spin text-[var(--ww-accent)]" />
        <p className="text-[14px] text-[var(--ww-text-2)]">Loading...</p>
      </div>
    )
  }

  // ── Non-guided intake: skip payment, just submit ────────────────────────
  if (!intakeDetails.isGuided) {
    const handleDirectSubmit = async () => {
      setIsSubmittingDirect(true)
      try {
        await onSubmit()
      } finally {
        setIsSubmittingDirect(false)
      }
    }

    return (
      <div className="flex-1 flex flex-col gap-5">
        <div className="flex items-center gap-3 px-4 py-3 rounded-2xl border border-[var(--ww-glass-border)] bg-[var(--ww-surface)] glass">
          <svg viewBox="0 0 24 24" fill="none" className="h-5 w-5 flex-shrink-0 text-[var(--ww-accent)]" stroke="currentColor" strokeWidth="1.5">
            <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
          <p className="text-[13px] text-[var(--ww-text-2)] leading-snug">
            Payment will be collected by your clinic. <span className="font-semibold text-[var(--ww-text)]">No payment is required now.</span>
          </p>
        </div>

        <div className="mt-auto pt-4 pb-2">
          <button
            type="button"
            disabled={isSubmittingDirect}
            onClick={() => { void handleDirectSubmit() }}
            className="flex w-full py-4 px-6 items-center justify-center gap-2 text-[15px] font-semibold text-[var(--ww-btn-text)] bg-[var(--ww-btn-bg)] border-none rounded-full cursor-pointer transition-all duration-300 shadow-[0_2px_8px_rgba(0,0,0,0.1)] hover:not-disabled:bg-[var(--ww-btn-hover)] hover:not-disabled:-translate-y-0.5 disabled:opacity-25 disabled:cursor-default"
          >
            {isSubmittingDirect ? (
              <>
                <Loader2 className="h-5 w-5 animate-spin" />
                Submitting...
              </>
            ) : (
              'Submit My Assessment'
            )}
          </button>
        </div>
      </div>
    )
  }

  // ── Guided intake with payment already held/captured (storefront flow) ──
  const paymentAlreadyOnFile = intakeDetails.isGuided && (intakeDetails.paymentStatus === 'hold_placed' || intakeDetails.paymentStatus === 'captured')
  if (paymentAlreadyOnFile) {
    const handleDirectSubmit = async () => {
      setIsSubmittingDirect(true)
      try {
        await onSubmit()
      } finally {
        setIsSubmittingDirect(false)
      }
    }

    return (
      <div className="flex-1 flex flex-col gap-5">
        <div className="flex items-center gap-3 px-4 py-3 rounded-2xl border border-[var(--ww-glass-border)] bg-[var(--ww-surface)] glass">
          <svg viewBox="0 0 24 24" fill="none" className="h-5 w-5 flex-shrink-0 text-[var(--ww-accent)]" stroke="currentColor" strokeWidth="1.5">
            <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
          <p className="text-[13px] text-[var(--ww-text-2)] leading-snug">
            Payment is already on file. <span className="font-semibold text-[var(--ww-text)]">You won't be charged again.</span>
          </p>
        </div>

        <div className="mt-auto pt-4 pb-2">
          <button
            type="button"
            disabled={isSubmittingDirect}
            onClick={() => { void handleDirectSubmit() }}
            className="flex w-full py-4 px-6 items-center justify-center gap-2 text-[15px] font-semibold text-[var(--ww-btn-text)] bg-[var(--ww-btn-bg)] border-none rounded-full cursor-pointer transition-all duration-300 shadow-[0_2px_8px_rgba(0,0,0,0.1)] hover:not-disabled:bg-[var(--ww-btn-hover)] hover:not-disabled:-translate-y-0.5 disabled:opacity-25 disabled:cursor-default"
          >
            {isSubmittingDirect ? (
              <>
                <Loader2 className="h-5 w-5 animate-spin" />
                Submitting...
              </>
            ) : (
              'Submit My Assessment'
            )}
          </button>
        </div>
      </div>
    )
  }

  // ── Guided intake: collect payment via Stripe ───────────────────────────

  if (error) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center gap-4 py-10">
        <div className="rounded-xl border border-red-500/30 bg-red-500/10 p-4 text-center text-sm text-red-300">
          {error}
        </div>
        <button
          type="button"
          onClick={() => {
            setClientSecret(null)
            setRetryCount((c) => c + 1)
          }}
          className="text-[14px] font-medium text-[var(--ww-accent)] underline underline-offset-2 cursor-pointer"
        >
          Try again
        </button>
      </div>
    )
  }

  if (!clientSecret) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center py-10">
        <p className="text-[14px] text-[var(--ww-text-2)]">Unable to initialize payment. Please try again.</p>
      </div>
    )
  }

  if (paymentConfirmed) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center gap-3 py-10">
        <Loader2 className="h-8 w-8 animate-spin text-[var(--ww-accent)]" />
        <p className="text-[14px] text-[var(--ww-text-2)]">Submitting your assessment...</p>
      </div>
    )
  }

  const elementsOptions: StripeElementsOptions = {
    clientSecret,
    // Customer Session unlocks saved-card display in PaymentElement.
    // Null-safe spread so we don't send `customerSessionClientSecret:
    // null` (which Stripe rejects) when the backend couldn't mint one.
    ...(customerSessionClientSecret ? { customerSessionClientSecret } : {}),
    appearance: {
      theme: 'night',
      variables: {
        colorPrimary: '#4A8A9A',
        colorBackground: 'rgba(255, 255, 255, 0.05)',
        colorText: '#e2e8f0',
        colorDanger: '#f87171',
        fontFamily: 'Inter, system-ui, sans-serif',
        spacingUnit: '4px',
        borderRadius: '12px',
        colorTextPlaceholder: 'rgba(255, 255, 255, 0.35)',
      },
      rules: {
        '.Input': {
          border: '1px solid rgba(255, 255, 255, 0.1)',
          backgroundColor: 'rgba(255, 255, 255, 0.05)',
        },
        '.Input:focus': {
          border: '1px solid rgba(45, 212, 191, 0.5)',
          boxShadow: '0 0 0 1px rgba(45, 212, 191, 0.25)',
        },
        '.Tab': {
          border: '1px solid rgba(255, 255, 255, 0.1)',
          backgroundColor: 'rgba(255, 255, 255, 0.03)',
        },
        '.Tab--selected': {
          border: '1px solid rgba(45, 212, 191, 0.4)',
          backgroundColor: 'rgba(45, 212, 191, 0.08)',
        },
        '.Label': {
          color: 'rgba(255, 255, 255, 0.7)',
        },
      },
    },
  }

  return (
    <div className="flex-1 flex flex-col gap-5">
      {/* Trust badge */}
      <div className="flex items-center gap-3 px-4 py-3 rounded-2xl border border-[var(--ww-glass-border)] bg-[var(--ww-surface)] glass">
        <svg viewBox="0 0 24 24" fill="none" className="h-5 w-5 flex-shrink-0 text-[var(--ww-accent)]" stroke="currentColor" strokeWidth="1.5">
          <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
        <p className="text-[13px] text-[var(--ww-text-2)] leading-snug">
          Your card is <span className="font-semibold text-[var(--ww-text)]">securely stored via Stripe</span> and will only be charged if your prescription is approved.
        </p>
      </div>

      {/* Stripe Elements */}
      <div className="rounded-2xl border border-[var(--ww-glass-border)] bg-[var(--ww-surface)] glass p-5">
        <Elements stripe={stripePromise} options={elementsOptions}>
          <PaymentFormInner onPaymentConfirmed={handlePaymentConfirmed} />
        </Elements>
      </div>
    </div>
  )
}
